import { useTranslation } from 'react-i18next';
import { LanguageSelect } from '@/components/LanguageSelect';
import { ThemeSelect } from '@/components/ThemeSelect';
import { ListGroup, ListRow, PageHeader } from '@/components/ui';
import { useAppVersion } from '@/features/app-update/useAppVersion';
import { QueueBar } from '@/features/upload-queue';
import { useLogout } from '@/hooks/useLogout';
import { visibleEntries } from '@/lib/routes/logic';
import { ROUTE_LABEL_KEY } from '@/lib/routes/route-labels';
import { useAuthStore } from '@/store/auth';

/**
 * 我的（CR-082 按示例重做）：账号 → 设置（外观、语言）→ 入口（修改密码、管理）→ 退出，一组一组的列表。
 * 改之前是一张一张的大卡片、每个入口后面一个「›」（客户不要箭头，CR-081）。
 */
export function MePage() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const role = user?.role ?? null;
  const handleLogout = useLogout();
  // 装的是哪一版（0.0.x，CR-068）；网页端是 null 就不显示
  const appVersion = useAppVersion();

  // 「管理」「修改密码」这些入口由路由表决定（CR-040）
  const menu = visibleEntries(role, 'meMenu');
  // 手机统一发放、多人轮用：打卡的人才需要「用完退出」这句提醒
  const punches = visibleEntries(role, 'homeCard').some((v) => v.route.id === 'm.punch');

  return (
    <>
      <PageHeader title={t('nav.me')} />

      <div className="mb-3">
        <QueueBar />
      </div>

      <ListGroup>
        <ListRow
          title={<span className="text-title font-semibold">{user?.name}</span>}
          subtitle={[role?.name, user?.employeeNo].filter(Boolean).join(' · ')}
        />
      </ListGroup>

      <ListGroup className="mt-5">
        {/* 外观（CR-081）：跟着这台手机走，不跟人走 */}
        <div className="flex min-h-row items-center justify-between gap-3 px-4 py-2">
          <span className="text-title text-ink">{t('settings.theme')}</span>
          <ThemeSelect hideLabel />
        </div>
        <div className="flex min-h-row items-center justify-between gap-3 px-4 py-2">
          <span className="text-title text-ink">{t('settings.language')}</span>
          <LanguageSelect className="w-36" />
        </div>
      </ListGroup>

      {menu.length > 0 && (
        <ListGroup className="mt-5">
          {menu.map(({ route }) => (
            <ListRow key={route.id} to={route.path} entry={route.id} title={t(ROUTE_LABEL_KEY[route.id])} />
          ))}
        </ListGroup>
      )}

      <ListGroup className="mt-5">
        <button
          type="button"
          data-action="logout"
          onClick={() => void handleLogout()}
          className="flex min-h-row w-full items-center justify-center px-4 text-title font-medium text-danger active:bg-surface-2"
        >
          {t('auth.logout')}
        </button>
      </ListGroup>

      {punches && (
        <p className="mt-3 text-center text-caption leading-relaxed text-ink-3">{t('auth.sharedDeviceHint')}</p>
      )}

      {appVersion && (
        <p className="mt-3 text-center text-caption text-ink-3">{t('settings.version', { version: appVersion })}</p>
      )}
    </>
  );
}
